import React, { useEffect, useState } from "react";
import CustomLoader from "../../../components/common/UI/Loader";
import { Button, Card, MenuProps, Dropdown, Table } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  MessageOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useUserProfileGate } from "../../../hooks/useUserProfileGate";
import { useThemeContext } from "../../../context/ThemeContext";
import BuyNumbers from "../BuyNumber";
import apiInstance from "../../../services/apiService";
import ENDPOINTS from "../../../services/endpoint";
import SettingsSidebarHeader from "../../../components/common/SettingsSidebarHeader";
import GradientButton from "../../../components/common/UI/GradientButton";
import Subscription from "../subscription";
import SMSAddons from "../../../components/Settings/Subscription/Addons/SMSAddon";
import "./index.scss";

interface MyNumbersProps {
  handleBackToPrevious: () => void;
}

const MyNumbers: React.FC<MyNumbersProps> = ({ handleBackToPrevious }) => {
  const { theme } = useThemeContext();
  const { gate, modal } = useUserProfileGate();
  const [currentScreen, setCurrentScreen] = useState("my-numbers");
  const [numbersData, setNumbersData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [registeredNumber, setRegisteredNumber] = useState("");
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(
    null,
  );

  const handleBackToMyNumbers = () => {
    setCurrentScreen("my-numbers");
  };

  const handleBuy = async () => {
    setIsLoading(true);
    try {
      const res = await gate();
      if (res) {
        setCurrentScreen("buy-numbers");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Define the action menu items using new v5 `menu` prop format
  const getActionMenu = (itemId: string): MenuProps => ({
    items: [
      {
        key: "delete",
        label: "Delete",
        icon: <DeleteOutlined style={{ color: "red" }} />,
        onClick: () => {
          setSelectedPhoneNumber(itemId);
          setCurrentScreen("subscription");
        },
      },
      {
        type: "divider",
      },
      {
        key: "sms",
        label: "Sms",
        icon: <MessageOutlined style={{ color: "blue" }} />,
        onClick: () => {
          setSelectedPhoneNumber(itemId);
          setCurrentScreen("sms-addon");
        },
      },
    ],
  });

  const columns = [
    {
      title: "Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 150,
    },
    {
      title: (
        <div className="table-header-icons">
          <img
            src={`/assets/icons/${theme}/free-call.png`}
            width={20}
            height={20}
            alt="free-call"
          />
        </div>
      ),
      key: "isFreeCall",
      dataIndex: "isFreeCall",
      render: (isFreeCall: boolean) => (
        <div className="check-and-cross-icons">
          {isFreeCall ? (
            <CheckOutlined style={{ color: "green" }} />
          ) : (
            <CloseOutlined style={{ color: "red" }} />
          )}
        </div>
      ),
      width: 70,
    },
    {
      title: (
        <div className="table-header-icons">
          <img
            src={`/assets/icons/${theme}/msg-in-out.png`}
            width={20}
            height={20}
            alt="msg-in-out"
          />
        </div>
      ),
      key: "isSMS",
      dataIndex: "isSMS",
      render: (isSMS: boolean) => (
        <div className="check-and-cross-icons">
          {isSMS ? (
            <CheckOutlined style={{ color: "green" }} />
          ) : (
            <CloseOutlined style={{ color: "red" }} />
          )}
        </div>
      ),
      width: 70,
    },
    {
      title: "",
      key: "actions",
      render: (item: any) => (
        <Dropdown
          menu={getActionMenu(item._id)}
          trigger={["click"]}
          overlayClassName="custom-dropdown"
        >
          <Button className="more-btn" icon={<MoreOutlined />} type="text" />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    const getPurchasedNumbers = async () => {
      setIsLoading(true);
      try {
        const response = await apiInstance.get(ENDPOINTS.GET_PURCHASED_NUMBERS);
        const data = response.data;
        setNumbersData(data.data);
        setRegisteredNumber(data.registedNumber);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    getPurchasedNumbers();
  }, [currentScreen]);

  return (
    <>
      {currentScreen === "my-numbers" && (
        <div className="my-numbers-container">
          <SettingsSidebarHeader
            title="My Numbers"
            handleBackClick={handleBackToPrevious}
          />

          <div className="secondary-section">
            <Card bordered={false} className="number-card">
              <span className="number-card-heading">eFone:</span>
              <span className="number-card-description">
                {registeredNumber}
              </span>
            </Card>

            <div className="table-container">
              <h2 className="table-heading">Purchased Numbers:</h2>
              <Table
                rowHoverable={false}
                className="data-table"
                columns={columns}
                dataSource={numbersData}
                pagination={false}
                scroll={{ y: 300 }}
                loading={{
                  spinning: isLoading,
                  indicator: (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <CustomLoader />
                    </div>
                  ),
                }}
                rowKey={(record) => record._id}
              />
            </div>

            <div className="buy-number-btn-container">
              <GradientButton
                loading={isLoading}
                className="buy-number-btn"
                onClick={handleBuy}
              >
                Buy Number
              </GradientButton>
            </div>
          </div>
        </div>
      )}

      {currentScreen === "buy-numbers" && (
        <BuyNumbers handleBackToPrevious={handleBackToMyNumbers} />
      )}

      {currentScreen === "subscription" && (
        <Subscription
          handleBackToPrevious={handleBackToMyNumbers}
          showOnlySubAddon={true}
          phoneNumberToDelete={selectedPhoneNumber || ""}
        />
      )}

      {currentScreen === "sms-addon" && (
        <SMSAddons
          handleBackToPrevious={handleBackToMyNumbers}
          phoneNumberToUpdate={selectedPhoneNumber || ""}
        />
      )}

      {modal}
    </>
  );
};

export default MyNumbers;
